import {
  RESET_BROADCAST_STREAM_START_FAILED_STATUS,
  RESET_BROADCAST_STREAM_STOP_FAILED_STATUS
} from '@shoflo/studio-redux/actions/types';
import {
  STREAM_START_FAILED,
  STREAM_STOP_FAILED
} from '@shoflo/studio-redux/constants/StreamStates';
import { isRecording } from '@shoflo/studio-redux/utils';

/**
 * @description Returns whether there's any endpoint with stream status as Failed.
 * @param {[]} broadcastStreamEndpoints
 * @param {string} status
 */
export function hasFailedStreamStatus(broadcastStreamEndpoints, status) {
  return broadcastStreamEndpoints?.some(stream => stream.status === status);
}

/**
 * @description Returns whether stop recording SLI has started.
 * @param {Set<>} sliStoppedRecordingIds
 * @param {{}} recording
 * @param {{}} currentUser
 */
export function hasStopRecordingSLIStarted(
  sliStoppedRecordingIds,
  recording,
  currentUser
) {
  return (
    !recording.isRTMPStream &&
    sliStoppedRecordingIds.has(recording._id) &&
    recording.userId === currentUser?._id
  );
}

/**
 * @description Returns whether recording has failed stopping.
 * @param {Set<>} sliStoppedRecordingIds
 * @param {{}} recording
 */
export function hasStopRecordingFailed(sliStoppedRecordingIds, recording) {
  return (
    sliStoppedRecordingIds.has(recording._id) &&
    recording.error?.isFailed &&
    recording.status === 'STARTED'
  );
}

/**
 * @description Called EndSLI method for stop/start stream and stop/start recording
 * @param {Set<>} sliIdsSet
 * @param {{}} match
 * @param {string} id
 * @param {function} endSLICallback
 * @param {String} errorMsg
 * @param {Boolean} isRecording
 */
export const endSLIWithPayload = (
  sliIdsSet,
  match,
  id,
  endSLICallback,
  errorMsg = null,
  isRecording
) => {
  let payload = {
    pathName: match.path,
    url: match.url,
    ...(isRecording ? { recordingId: id } : { streamId: id })
  };
  payload = errorMsg ? { ...payload, error: errorMsg } : payload;
  endSLICallback(payload);
  sliIdsSet.delete(id);
};

/**
 * @description Ends the Stop Recording SLI.
 * @param {[]} allRecordings
 * @param {Set<>} sliStoppedRecordingIds
 * @param {{}} match
 * @param {function} endStopRecordingSLICallback
 * @param {{}} match
 * @param {{}} currentUser
 */
export function endStopRecordingSLI(
  allRecordings,
  sliStoppedRecordingIds,
  match,
  endStopRecordingSLICallback,
  currentUser
) {
  allRecordings.forEach(recording => {
    if (hasStopRecordingFailed(sliStoppedRecordingIds, recording)) {
      endSLIWithPayload(
        sliStoppedRecordingIds,
        match,
        recording._id,
        endStopRecordingSLICallback,
        recording.error.errorMessage,
        true
      );
    } else if (
      hasStopRecordingSLIStarted(sliStoppedRecordingIds, recording, currentUser)
    ) {
      endSLIWithPayload(
        sliStoppedRecordingIds,
        match,
        recording._id,
        endStopRecordingSLICallback,
        null,
        true
      );
    }
  });
}

/**
 * @description Returns whether stream has started.
 * @param {[]} sliStartedStreamIds
 * @param {string} streamId
 * @param {string} status
 */
export const hasStreamStarted = (sliStartedStreamIds, streamId, status) => {
  return sliStartedStreamIds.has(streamId) && status === 'STARTED';
};

/**
 * @description Returns whether stream has failed starting.
 * @param {[]} sliStartedStreamIds
 * @param {string} streamId
 * @param {string} status
 * @param {{}} error
 */
export const hasStreamStartFailed = (sliStartedStreamIds, streamId, status) => {
  return sliStartedStreamIds.has(streamId) && status === STREAM_START_FAILED;
};

/**
 * @description Ends Stream Start SLI
 * @param {[]} broadcastStreamEndpoints
 * @param {{}} sliStartedStreamIds
 * @param {function} endStreamStartLifecycle
 * @param {{}} match
 * @param {{}} disptach
 */
export const endStreamStartSli = (
  broadcastStreamEndpoints,
  sliStartedStreamIds,
  endStreamStartLifecycle,
  match,
  dispatch
) => {
  let hasSliStopped = false;
  broadcastStreamEndpoints.forEach(endpoint => {
    if (hasStreamStarted(sliStartedStreamIds, endpoint._id, endpoint.status)) {
      endSLIWithPayload(
        sliStartedStreamIds,
        match,
        endpoint._id,
        endStreamStartLifecycle,
        null
      );
      hasSliStopped = true;
    } else if (
      hasStreamStartFailed(sliStartedStreamIds, endpoint._id, endpoint.status)
    ) {
      endSLIWithPayload(
        sliStartedStreamIds,
        match,
        endpoint._id,
        endStreamStartLifecycle,
        endpoint.error?.errorMessage
      );
      hasSliStopped = true;
    }
  });
  if (
    hasSliStopped &&
    hasFailedStreamStatus(broadcastStreamEndpoints, STREAM_START_FAILED)
  ) {
    dispatch({
      type: RESET_BROADCAST_STREAM_START_FAILED_STATUS,
      payload: { endpoints: broadcastStreamEndpoints }
    });
  }
};

/**
 * @description Returns whether stream has stopped.
 * @param {[]} sliStoppedStreamIds
 * @param {string} streamId
 * @param {string} status
 */
export const hasStreamStopped = (sliStoppedStreamIds, streamId, status) => {
  return sliStoppedStreamIds.has(streamId) && status === 'STOPPED';
};

/**
 * @description Returns whether stream has failed stopping.
 * @param {[]} sliStoppedStreamIds
 * @param {string} streamId
 * @param {string} status
 * @param {{}} error
 */
export const hasStreamStopFailed = (sliStoppedStreamIds, streamId, status) => {
  return sliStoppedStreamIds.has(streamId) && status === STREAM_STOP_FAILED;
};

/**
 * @description Ends Stream Stop SLI
 * @param {[]} broadcastStreamEndpoints
 * @param {{}} sliStoppedStreamIds
 * @param {function} endStreamStopLifecycle
 * @param {{}} match
 * @param {{}} disptach
 */
export const endStreamStopSli = (
  broadcastStreamEndpoints,
  sliStoppedStreamIds,
  endStreamStopLifecycle,
  match,
  dispatch
) => {
  let hasSliStopped = false;
  broadcastStreamEndpoints.forEach(endpoint => {
    if (hasStreamStopped(sliStoppedStreamIds, endpoint._id, endpoint.status)) {
      endSLIWithPayload(
        sliStoppedStreamIds,
        match,
        endpoint._id,
        endStreamStopLifecycle,
        null
      );
      hasSliStopped = true;
    } else if (
      hasStreamStopFailed(sliStoppedStreamIds, endpoint._id, endpoint.status)
    ) {
      endSLIWithPayload(
        sliStoppedStreamIds,
        match,
        endpoint._id,
        endStreamStopLifecycle,
        endpoint.error?.errorMessage
      );
      hasSliStopped = true;
    }
  });
  if (
    hasSliStopped &&
    hasFailedStreamStatus(broadcastStreamEndpoints, STREAM_STOP_FAILED)
  ) {
    dispatch({
      type: RESET_BROADCAST_STREAM_STOP_FAILED_STATUS,
      payload: { endpoints: broadcastStreamEndpoints }
    });
  }
};

/**
 * @description Returns the current recording SLI failure status
 * @param {Set<>} sliStartedRecordingIds
 * @param {{}} recording
 */
export function hasStartRecordingSLIFailed(sliStartedRecordingIds, recording) {
  return sliStartedRecordingIds.has(recording._id) && recording.error?.isFailed;
}

/**
 * @description check recording SLI start status for current recording
 * @param {{}} recording
 * @param {Set<>}sliStartedRecordingIds
 */
export function hasStartRecordingSLIStarted(recording, sliStartedRecordingIds) {
  return (
    !recording.isRTMPStream &&
    isRecording(recording) &&
    sliStartedRecordingIds.has(recording?._id)
  );
}

/**
 * @description End the Start Recording SLI
 * @param {[]} allRecordings
 * @param {Set<>} sliStartedRecordingIds
 * @param {{}} match
 * @param {function} endRecordingStarSLIWorkflow
 */
export function endStartRecordingSLI(
  allRecordings,
  sliStartedRecordingIds,
  match,
  endRecordingStarSLIWorkflow
) {
  allRecordings.forEach(recording => {
    if (hasStartRecordingSLIFailed(sliStartedRecordingIds, recording)) {
      endSLIWithPayload(
        sliStartedRecordingIds,
        match,
        recording._id,
        endRecordingStarSLIWorkflow,
        recording.error.errorMessage,
        true
      );
    } else if (hasStartRecordingSLIStarted(recording, sliStartedRecordingIds)) {
      endSLIWithPayload(
        sliStartedRecordingIds,
        match,
        recording._id,
        endRecordingStarSLIWorkflow,
        null,
        true
      );
    }
  });
}

/**
 * @description Splits recordings into past and live recordings
 * @param recordings
 * @returns {{pastRecordings: *, liveRecordings: *}}
 */
export const splitRecordingsByStatus = recordings => {
  const liveRecordings = [];
  const pastRecordings = [];

  recordings.forEach(rec => {
    if (rec.endedAt) {
      pastRecordings.push(rec);
    } else if (!rec.endedAt) {
      liveRecordings.push(rec);
    }
  });

  return { liveRecordings, pastRecordings };
};

/**
 * @description Updates the recording list with the new recording
 * @param recordings
 * @param recordingFromPayload
 * @returns {*|*[]}
 */
export const updateRecordingsFromPayload = (
  recordings,
  recordingFromPayload
) => {
  const existingRecording = recordings.find(
    recording => recording._id === recordingFromPayload._id
  );

  if (existingRecording) {
    return recordings.map(recording => {
      if (recording._id === recordingFromPayload._id) {
        return recordingFromPayload;
      }
      return recording;
    });
  }

  return [...recordings, recordingFromPayload];
};
